/**
 * Contact Type Tags Service
 * Service for managing the many-to-many relationship between Clients, Contacts, and ContactTypes
 */

import { BaseService } from './BaseService';

/**
 * Helper class for working with contact type tags
 */
export class ContactTypeTagsHelper {
    /**
     * Check if a contact has a specific type
     * @param {Array} tags - Array of ContactTypeTagsDto
     * @param {number} contactTypeId - Contact type ID to check
     * @returns {boolean}
     */
    static hasContactType(tags, contactTypeId) {
        return tags.some(tag => tag.contactTypeId === contactTypeId);
    }

    /**
     * Get contact type IDs from tags
     * @param {Array} tags - Array of ContactTypeTagsDto
     * @returns {Array<number>} Array of contact type IDs
     */
    static getContactTypeIds(tags) {
        return tags.map(tag => tag.contactTypeId);
    }

    /**
     * Get contact type names from tags
     * @param {Array} tags - Array of ContactTypeTagsDto
     * @returns {Array<string>} Array of contact type names
     */
    static getContactTypeNames(tags) {
        return tags.map(tag => tag.contactType).filter(Boolean);
    }

    /**
     * Group tags by contact
     * @param {Array} tags - Array of ContactTypeTagsDto
     * @returns {Object} Tags grouped by contactId
     */
    static groupByContact(tags) {
        return tags.reduce((acc, tag) => {
            if (!acc[tag.contactId]) {
                acc[tag.contactId] = [];
            }
            acc[tag.contactId].push(tag);
            return acc;
        }, {});
    }

    /**
     * Group tags by contact type
     * @param {Array} tags - Array of ContactTypeTagsDto
     * @returns {Object} Tags grouped by contactTypeId
     */
    static groupByContactType(tags) {
        return tags.reduce((acc, tag) => {
            if (!acc[tag.contactTypeId]) {
                acc[tag.contactTypeId] = [];
            }
            acc[tag.contactTypeId].push(tag);
            return acc;
        }, {});
    }
}

/**
 * Contact Type Tags Service Class
 */
export class ContactTypeTagsService extends BaseService {
    constructor() {
        super('/api/contactTypeTags');
    }

    /**
     * Get all contact type tags
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of tags
     */
    async getAllTags(accessToken) {
        try {
            return await this.makeRequest(accessToken, '');
        } catch (error) {
            console.error('Failed to fetch all contact type tags:', error);
            throw error;
        }
    }

    /**
     * Get specific contact type tag
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactId - Contact ID
     * @param {number} contactTypeId - Contact type ID
     * @returns {Promise<Object|null>} Tag object or null if not found
     */
    async getTag(accessToken, clientId, contactId, contactTypeId) {
        try {
            return await this.makeRequest(accessToken, `/${clientId}/${contactId}/${contactTypeId}`);
        } catch (error) {
            if (error.message && error.message.includes('404')) {
                return null;
            }
            console.error('Failed to fetch contact type tag:', error);
            throw error;
        }
    }

    /**
     * Get all tags for a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of tags
     */
    async getTagsByClient(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}`);
        } catch (error) {
            console.error(`Failed to fetch tags for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get all tags for a contact
     * @param {string} accessToken - JWT access token
     * @param {number} contactId - Contact ID
     * @returns {Promise<Array>} Array of tags
     */
    async getTagsByContact(accessToken, contactId) {
        try {
            return await this.makeRequest(accessToken, `/contact/${contactId}`);
        } catch (error) {
            console.error(`Failed to fetch tags for contact ${contactId}:`, error);
            throw error;
        }
    }

    /**
     * Get all tags for a contact type
     * @param {string} accessToken - JWT access token
     * @param {number} contactTypeId - Contact type ID
     * @returns {Promise<Array>} Array of tags
     */
    async getTagsByContactType(accessToken, contactTypeId) {
        try {
            return await this.makeRequest(accessToken, `/contactType/${contactTypeId}`);
        } catch (error) {
            console.error(`Failed to fetch tags for contact type ${contactTypeId}:`, error);
            throw error;
        }
    }

    /**
     * Get contact types for a client-contact pair
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactId - Contact ID
     * @returns {Promise<Array>} Array of tags
     */
    async getContactTypesForClientContact(accessToken, clientId, contactId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/contact/${contactId}`);
        } catch (error) {
            console.error(`Failed to fetch contact types for client ${clientId} contact ${contactId}:`, error);
            throw error;
        }
    }

    /**
     * Get contacts for a client-contact type pair
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactTypeId - Contact type ID
     * @returns {Promise<Array>} Array of tags
     */
    async getContactsForClientContactType(accessToken, clientId, contactTypeId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/contactType/${contactTypeId}`);
        } catch (error) {
            console.error(`Failed to fetch contacts for client ${clientId} contact type ${contactTypeId}:`, error);
            throw error;
        }
    }

    /**
     * Check if a tag relationship exists
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactId - Contact ID
     * @param {number} contactTypeId - Contact type ID
     * @returns {Promise<boolean>} True if exists
     */
    async tagExists(accessToken, clientId, contactId, contactTypeId) {
        try {
            const response = await this.makeRequest(accessToken, `/exists/${clientId}/${contactId}/${contactTypeId}`);
            return response.exists === true;
        } catch (error) {
            console.error('Failed to check if tag exists:', error);
            throw error;
        }
    }

    /**
     * Get count of contact types for a client-contact
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactId - Contact ID
     * @returns {Promise<number>} Count
     */
    async getContactTypeCountForClientContact(accessToken, clientId, contactId) {
        try {
            const response = await this.makeRequest(accessToken, `/client/${clientId}/contact/${contactId}/count`);
            return response.count || 0;
        } catch (error) {
            console.error('Failed to get contact type count:', error);
            throw error;
        }
    }

    /**
     * Get count of contacts for a client-contact type
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactTypeId - Contact type ID
     * @returns {Promise<number>} Count
     */
    async getContactCountForClientContactType(accessToken, clientId, contactTypeId) {
        try {
            const response = await this.makeRequest(accessToken, `/client/${clientId}/contactType/${contactTypeId}/count`);
            return response.count || 0;
        } catch (error) {
            console.error('Failed to get contact count:', error);
            throw error;
        }
    }

    /**
     * Create a single tag
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactId - Contact ID
     * @param {number} contactTypeId - Contact type ID
     * @returns {Promise<Object>} Created tag
     */
    async createTag(accessToken, clientId, contactId, contactTypeId) {
        try {
            return await this.makeRequest(accessToken, '', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ clientId, contactId, contactTypeId })
            });
        } catch (error) {
            console.error('Failed to create contact type tag:', error);
            throw error;
        }
    }

    /**
     * Assign multiple contact types to a client-contact
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactId - Contact ID
     * @param {Array<number>} contactTypeIds - Array of contact type IDs
     * @returns {Promise<Array>} Array of created tags
     */
    async assignContactTypesToContact(accessToken, clientId, contactId, contactTypeIds) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/contact/${contactId}/assign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(contactTypeIds)
            });
        } catch (error) {
            console.error('Failed to assign contact types:', error);
            throw error;
        }
    }

    /**
     * Assign multiple contacts to a client-contact type
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactTypeId - Contact type ID
     * @param {Array<number>} contactIds - Array of contact IDs
     * @returns {Promise<Array>} Array of created tags
     */
    async assignContactsToContactType(accessToken, clientId, contactTypeId, contactIds) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/contactType/${contactTypeId}/assign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(contactIds)
            });
        } catch (error) {
            console.error('Failed to assign contacts:', error);
            throw error;
        }
    }

    /**
     * Update (replace) contact types for a client-contact
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactId - Contact ID
     * @param {Array<number>} contactTypeIds - Array of contact type IDs
     * @returns {Promise<Array>} Array of updated tags
     */
    async updateContactTypesForContact(accessToken, clientId, contactId, contactTypeIds) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/contact/${contactId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(contactTypeIds)
            });
        } catch (error) {
            console.error('Failed to update contact types:', error);
            throw error;
        }
    }

    /**
     * Update (replace) contacts for a client-contact type
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactTypeId - Contact type ID
     * @param {Array<number>} contactIds - Array of contact IDs
     * @returns {Promise<Array>} Array of updated tags
     */
    async updateContactsForContactType(accessToken, clientId, contactTypeId, contactIds) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/contactType/${contactTypeId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(contactIds)
            });
        } catch (error) {
            console.error('Failed to update contacts:', error);
            throw error;
        }
    }

    /**
     * Delete a single tag
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactId - Contact ID
     * @param {number} contactTypeId - Contact type ID
     * @returns {Promise<boolean>} Success status
     */
    async deleteTag(accessToken, clientId, contactId, contactTypeId) {
        try {
            await this.makeRequest(accessToken, `/${clientId}/${contactId}/${contactTypeId}`, {
                method: 'DELETE'
            });
            return true;
        } catch (error) {
            console.error('Failed to delete contact type tag:', error);
            throw error;
        }
    }

    /**
     * Delete all tags for a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<boolean>} Success status
     */
    async deleteTagsByClient(accessToken, clientId) {
        try {
            await this.makeRequest(accessToken, `/client/${clientId}`, {
                method: 'DELETE'
            });
            return true;
        } catch (error) {
            console.error(`Failed to delete tags for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Delete all tags for a contact
     * @param {string} accessToken - JWT access token
     * @param {number} contactId - Contact ID
     * @returns {Promise<boolean>} Success status
     */
    async deleteTagsByContact(accessToken, contactId) {
        try {
            await this.makeRequest(accessToken, `/contact/${contactId}`, {
                method: 'DELETE'
            });
            return true;
        } catch (error) {
            console.error(`Failed to delete tags for contact ${contactId}:`, error);
            throw error;
        }
    }

    /**
     * Delete all tags for a contact type
     * @param {string} accessToken - JWT access token
     * @param {number} contactTypeId - Contact type ID
     * @returns {Promise<boolean>} Success status
     */
    async deleteTagsByContactType(accessToken, contactTypeId) {
        try {
            await this.makeRequest(accessToken, `/contactType/${contactTypeId}`, {
                method: 'DELETE'
            });
            return true;
        } catch (error) {
            console.error(`Failed to delete tags for contact type ${contactTypeId}:`, error);
            throw error;
        }
    }

    /**
     * Delete all tags for a client-contact pair
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} contactId - Contact ID
     * @returns {Promise<boolean>} Success status
     */
    async deleteTagsForClientContact(accessToken, clientId, contactId) {
        try {
            await this.makeRequest(accessToken, `/client/${clientId}/contact/${contactId}`, {
                method: 'DELETE'
            });
            return true;
        } catch (error) {
            console.error(`Failed to delete tags for client ${clientId} contact ${contactId}:`, error);
            throw error;
        }
    }
}

// Create singleton instance
const contactTypeTagsService = new ContactTypeTagsService();

// Export both named and default for flexibility
export { contactTypeTagsService };
export default contactTypeTagsService;
